import { NetworkResponse } from "~/app/type/common";
import { requestREST } from "~/client/http/CommonHTTP";
import {
  IDealerEmployee,
  IMigrateToStaffResponse,
  IUserListExportResponse,
  IUserMe,
  IUserResponse,
  userListFilterIProps,
  UserListIProps,
} from "~/entity/Users/model/schema";

const usersServiceMap = {
  showMyUser: "/user/me",
  userList: "/user/list",
  migrateToStaff: "/user/migrate-to-staff",
  dealerEmployees: "/dealer/employees",
  // PUT /user/temporary-assignments — полная запись временных закреплений.
  temporaryAssignments: "/user/temporary-assignments",
  userAssignments: (userId: number | string) => `/user/${userId}/assignments`,
  availableRegionalManagers: "/user/available-regional-managers",
  user: "/user",
};

export interface IAssignmentItem {
  userId: number;
  dealerIds: number[];
  assignmentType: "temporary";
  // temporaryRmId (НА кого назначено) не шлём — сервер сам ставит userId.
  // permanentRmId — ОТНОСИТЕЛЬНО кого назначаем (РМ из селекта строки).
  permanentRmId?: number;
  startDate?: string;
  endDate?: string | null;
}

// PUT /user/temporary-assignments — полная запись временных закреплений.
export interface IUpdateTemporaryAssignmentsPayload {
  userId: number;
  assignments: IAssignmentItem[];
}

// Ответ PUT /user/temporary-assignments (и тело message при HTTP 400):
// частичные ошибки приходят в results.errors массивом строк.
export interface IUpdateTemporaryAssignmentsResponse {
  success: boolean;
  message: string;
  results: {
    created: unknown[];
    updated: unknown[];
    deleted: unknown[];
    errors: string[];
  };
}

// GET /user/temporary-assignments — список всех временных закреплений
// (используем, чтобы исключить из выбора дилеров, уже назначенных другим).
export interface ITemporaryAssignmentsListParams {
  userId?: number;
  dealerId?: number;
  status?: string;
  page?: number; // 1-indexed
  limit?: number;
}

export interface ITemporaryAssignmentListItem {
  id: number;
  UserId: number;
  DealerId: number;
  assignmentType: string;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  isScheduled: boolean;
  isExpired: boolean;
  user?: { id: number; fullname: string; login: string };
  dealer?: { id: number; name: string };
}

export interface ITemporaryAssignmentsListResponse {
  items: ITemporaryAssignmentListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface IUserAssignmentDealer {
  id: number;
  name?: string;
  inn?: string;
  code?: string;
  status?: string;
  city?: string;
  fo?: string;
}

// API группирует закрепления по (temporaryRmId, startDate, endDate) и отдаёт
// одну запись на группу с массивом дилеров внутри (dealerIds[] / dealers[]).
export interface IUserAssignmentItem {
  dealerIds?: number[];
  dealers?: IUserAssignmentDealer[];
  dealerId?: number; // legacy — одиночный дилер (старый формат, для совместимости)
  temporaryRmId?: number | null;
  temporaryRmName?: string | null;
  temporaryRmLogin?: string | null;
  permanentRmId?: number | null;
  permanentRmName?: string | null;
  permanentRmLogin?: string | null;
  permanentRmDirections?: {
    id: number;
    name: string;
    instanceId: number;
    leader?: boolean;
  }[];
  assignmentType: "permanent" | "temporary";
  startDate?: string | null;
  endDate?: string | null;
  createdAt?: string;
  isActive?: boolean;
  isScheduled?: boolean;
  isExpired?: boolean;
}

export interface IUserAssignmentsSummary {
  total?: number;
  permanentCount?: number;
  temporaryCount?: number;
  activeTemporaryCount?: number;
  scheduledTemporaryCount?: number;
  expiredTemporaryCount?: number;
}

export interface IUserAssignmentsResponse {
  userId: number;
  user?: Record<string, unknown>;
  permanent: IUserAssignmentItem[];
  temporary: IUserAssignmentItem[];
  summary?: IUserAssignmentsSummary;
}

// GET /user/available-regional-managers
export interface IRegionalManagerDirection {
  id: number;
  name: string;
  instanceId: number;
}

export interface IRegionalManagerDealer {
  id: number;
  dealerName: string;
  code?: string;
  inn?: string;
}

export interface IRegionalManagerOption {
  id: number;
  login: string;
  email: string;
  fullname: string;
  phone: string;
  status: string;
  position: string;
  roleId: string;
  roleName: string;
  isRegional: boolean;
  isLeaderRegional: boolean;
  directions: IRegionalManagerDirection[];
  dealers: IRegionalManagerDealer[];
}

export interface IAvailableRegionalManagersResponse {
  items: IRegionalManagerOption[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface IAvailableRegionalManagersParams {
  userId?: number;
  search?: string;
  dealerCode?: string;
  excludeUserIds?: string;
  page?: number; // 1-indexed
  limit?: number;
}

const postUserList = <T>(params: userListFilterIProps): NetworkResponse<T> =>
  requestREST.post(usersServiceMap.userList, {
    ...params,
    export: Boolean(params.export),
  });

export class UsersService {
  static GET = {
    getMyUser: (): NetworkResponse<IUserMe> =>
      requestREST.get(usersServiceMap.showMyUser, {}),
    getDealerEmployees: (
      dealerId: string | number,
    ): NetworkResponse<IDealerEmployee[]> =>
      requestREST.get(usersServiceMap.dealerEmployees, {
        params: { dealerId },
      }),
    getUserAssignments: (
      userId: number | string,
    ): NetworkResponse<IUserAssignmentsResponse> =>
      requestREST.get(usersServiceMap.userAssignments(userId), {}),
    getAvailableRegionalManagers: (
      params: IAvailableRegionalManagersParams,
    ): NetworkResponse<IAvailableRegionalManagersResponse> =>
      requestREST.get(usersServiceMap.availableRegionalManagers, { params }),
    getTemporaryAssignmentsList: (
      params: ITemporaryAssignmentsListParams,
    ): NetworkResponse<ITemporaryAssignmentsListResponse> =>
      requestREST.get(usersServiceMap.temporaryAssignments, { params }),
    getUser: (id: string | number): NetworkResponse<IUserResponse> =>
      requestREST.get(`${usersServiceMap.user}/${id}`, {}),
  };

  static POST = {
    getUserList: (params: userListFilterIProps) =>
      postUserList<UserListIProps>({ ...params, export: false }),
    exportUserList: (params: userListFilterIProps) =>
      postUserList<IUserListExportResponse>({ ...params, export: true }),
    migrateToStaff: (
      userIds: number[],
    ): NetworkResponse<IMigrateToStaffResponse> =>
      requestREST.post(usersServiceMap.migrateToStaff, { userIds }),
  };

  static PUT = {
    updateTemporaryAssignments: (
      payload: IUpdateTemporaryAssignmentsPayload,
    ): NetworkResponse<IUpdateTemporaryAssignmentsResponse> =>
      requestREST.put(usersServiceMap.temporaryAssignments, payload),
  };
}
